// De pil-as: wie is er aan het woord (owner-besluit 26-08).
//
// De dertien redactievormen zijn machinerie — welk recept zocht welk materiaal.
// De PIL zegt iets anders: van wie is de zin die je leest. Dat is de as die de
// lezer herkent, en tegelijk de eerlijkheidsas van het platform: bij "Rapporten"
// spreekt de bron, bij "Synthese" spreken wij.
//
// Deze module verzint niets. Hij groepeert bestaande vormen en leest bestaande
// grond; waar een laag ontbreekt geeft hij de reden terug in plaats van niets.

import type { EditieKaartData, KaartBron } from "../contracts";
import { heeftBewijsvelden, wieMaal } from "../components/Bewijs";

export type PilId = "synthese" | "trends" | "skills" | "rapporten" | "subsidies" | "bedrijven";

export type Pil = {
  id: PilId;
  label: string;
  /** Wie er aan het woord is — staat in de uitleg onder de pil. */
  spreker: string;
  kleur: string;
  /** Het hoofdstuk als de stroom in hoofdstukken valt. */
  hoofdstuk: string;
  /** Alleen gevuld voor bakken die hun databron nog niet hebben. */
  reden?: string;
};

export const PILLEN: Record<PilId, Pil> = {
  synthese: {
    id: "synthese",
    label: "Synthese",
    spreker: "onze analyse over de bronnen heen",
    kleur: "#7c3aed",
    hoofdstuk: "Wat wij eruit lezen",
  },
  trends: {
    id: "trends",
    label: "Trends",
    spreker: "het patroon in het corpus",
    kleur: "#2563eb",
    hoofdstuk: "Wat er beweegt",
  },
  skills: {
    id: "skills",
    label: "Skills",
    spreker: "de meting",
    kleur: "#059669",
    hoofdstuk: "Welke vaardigheden opkomen",
  },
  rapporten: {
    id: "rapporten",
    label: "Rapporten",
    spreker: "de bron zelf",
    kleur: "#0891b2",
    hoofdstuk: "Wat de nieuwe rapporten zeggen",
  },
  subsidies: {
    id: "subsidies",
    label: "Subsidies",
    spreker: "de officiële regelingpagina",
    kleur: "#d97706",
    hoofdstuk: "Welk geld er openstaat",
    reden:
      "Subsidiesignalen komen uit de gebeurtenis-feed, niet uit de editie. Deze momentopname draagt " +
      "ze niet; in de app staan hier de vensters die openen of sluiten.",
  },
  bedrijven: {
    id: "bedrijven",
    label: "Bedrijven",
    spreker: "—",
    kleur: "#9ca3af",
    hoofdstuk: "Wat er bij je bedrijven speelt",
    reden:
      "De bedrijvenlaan is ontworpen (de Bedrijfsspiegel uit W4) maar niet gebouwd: geen " +
      "bedrijvenregister, geen vacature-extractie, geen skill-gap-analyse.",
  },
};

/** De volgorde op het scherm: onze eigen stem eerst, dan wat de bronnen zeggen. */
export const PIL_VOLGORDE: PilId[] = ["synthese", "trends", "skills", "rapporten", "subsidies", "bedrijven"];

const VORM_NAAR_PIL: Record<string, PilId> = {
  keten: "synthese",
  // De scenariokaart doortrekt wat wij gemeten hebben; geen enkele bron zegt
  // het. Dat is per definitie onze eigen stem, dus synthese — en op de kaart
  // zelf staat het amber label dat zegt dat het een doortrekking is.
  scenariokaart: "synthese",
  witte_vlek: "synthese",
  bevestigde_trend: "synthese",
  spanning: "synthese",
  verschuiving: "synthese",
  vraag_aan_jou: "synthese",
  regionale_afwijking: "synthese",
  regelgevingsradar: "trends",
  cijfer_van_de_maand: "trends",
  vaardigheid_in_beweging: "skills",
  rapport_aan_het_woord: "rapporten",
  zwaar_rapport: "rapporten",
  citaat: "rapporten",
};

export function pilVan(kaart: EditieKaartData): Pil {
  return PILLEN[VORM_NAAR_PIL[kaart.vorm] ?? "synthese"];
}

type Grond = Record<string, unknown>;
const grondVan = (k: EditieKaartData): Grond => (k.grond ?? {}) as Grond;

/* ── bronnen ─────────────────────────────────────────────────────────────── */

export type Bronbundel = {
  /** Eén rij per DOCUMENT, niet per bewijsregel. */
  bron: KaartBron;
  /** Hoeveel passages dit document levert. */
  passages: number;
};

/**
 * Ontdubbelen op document. `bronnen[]` is een lijst bewijsREGELS: hetzelfde
 * rapport staat er twee of drie keer in als het meerdere citaten levert.
 * Gemeten over de editie van augustus: 9 van de 83 rijen zijn zo'n duplicaat.
 */
export function bronbundels(kaart: EditieKaartData): Bronbundel[] {
  const perDoc = new Map<string, Bronbundel>();
  for (const b of kaart.bronnen ?? []) {
    // documentId is in het contract optioneel; val terug op de url en anders op
    // de uitgever, zodat ontdubbelen nooit stilvalt.
    const sleutel = b.documentId ?? b.url ?? b.uitgever;
    const bestaand = perDoc.get(sleutel);
    if (bestaand) {
      bestaand.passages += 1;
      // Het citaat van de eerste rij blijft staan; alleen als die er geen had
      // neemt een latere rij zijn plek in.
      if (!bestaand.bron.quote && b.quote) bestaand.bron = b;
    } else {
      perDoc.set(sleutel, { bron: b, passages: 1 });
    }
  }
  // Zwaarste uitgever vooraan: dat is wat er in de pil komt te staan.
  return [...perDoc.values()].sort((a, b) => (b.bron.gezag ?? 0) - (a.bron.gezag ?? 0));
}

/** De terugvalladder voor een brontitel — nooit twee keer het domein. */
export function bronTitel(b: KaartBron): string {
  if (b.titel && b.titel.trim() && b.titel.trim() !== b.uitgever) return b.titel;
  if (b.url) {
    try {
      const u = new URL(b.url);
      return `Rapport zonder titel — ${u.host}${u.pathname}`;
    } catch {
      /* naar de laatste trede */
    }
  }
  return "Rapport zonder titel";
}

export const datumNL = (d?: string | null): string => {
  if (!d) return "";
  const maanden = ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${Number(m[3])} ${maanden[Number(m[2]) - 1]} ${m[1]}` : d;
};

/* ── het visuele blok ────────────────────────────────────────────────────── */

export type Visueel =
  | { soort: "reeks"; punten: { periode: string; waarde: number }[]; waarde: number; eerder: number | null; eenheid: string }
  | { soort: "scores"; prioriteit: number | null; impact: number | null; effort: number | null }
  | { soort: "datum"; label: string; maanden: number | null }
  | { soort: "voedt"; trends: number; skills: number }
  | null;

/**
 * Het sterkste visueel dat deze kaart eerlijk kan dragen. Volgorde: een echte
 * reeks verslaat scores, scores verslaan een datum. Is er niets, dan geeft hij
 * null terug en toont de kaart geen blok — geen leeg vak, geen verzonnen lijn.
 */
export function visueelVan(kaart: EditieKaartData): Visueel {
  const g = grondVan(kaart);

  const reeks = g.reeks as
    | { punten?: { periode: string; waarde: number }[]; waarde?: number; jaar_eerder?: number; eenheid?: string }
    | undefined;
  if (reeks?.punten && reeks.punten.length >= 2 && typeof reeks.waarde === "number") {
    return {
      soort: "reeks",
      punten: reeks.punten,
      waarde: reeks.waarde,
      eerder: typeof reeks.jaar_eerder === "number" ? reeks.jaar_eerder : null,
      eenheid: String(reeks.eenheid ?? ""),
    };
  }

  const scores = g.scores as Record<string, number> | undefined;
  if (scores && (scores.prioriteit != null || scores.impact != null)) {
    return {
      soort: "scores",
      prioriteit: scores.prioriteit ?? null,
      impact: scores.impact ?? null,
      effort: scores.effort ?? null,
    };
  }

  const peildatum = g.peildatum as string | undefined;
  if (peildatum) {
    const m = /^(\d{4})-(\d{2})/.exec(peildatum);
    let maanden: number | null = null;
    if (m) {
      // De momentopname is van augustus 2026; dat is de nulmeting van dit scherm.
      maanden = (Number(m[1]) - 2026) * 12 + (Number(m[2]) - 8);
      if (maanden < 0) maanden = null;
    }
    return { soort: "datum", label: String(g.peildatum_bron ?? peildatum), maanden };
  }

  const voedt = g.voedt as { trends?: number; skills?: number } | undefined;
  if (voedt?.trends != null) {
    return { soort: "voedt", trends: voedt.trends, skills: voedt.skills ?? 0 };
  }

  return null;
}

/* ── de lagenmeter (V7) ──────────────────────────────────────────────────── */

export type Laag = { label: string; waarde: string; gemeten: boolean };

export function lagenVan(kaart: EditieKaartData): Laag[] {
  const g = grondVan(kaart);
  const trend = g.trend as Record<string, unknown> | undefined;
  const am = (g.arbeidsmarkt as Record<string, unknown>[] | undefined) ?? [];
  const subs = (g.subsidie as Record<string, unknown>[] | undefined) ?? [];
  const vaardigheden = (g.vaardigheden as Record<string, unknown>[] | undefined) ?? [];

  return [
    trend
      ? { label: "Trend", waarde: `${String(trend.bronnen ?? "?")} bronnen`, gemeten: true }
      : { label: "Trend", waarde: "geen trend", gemeten: false },
    am.length > 0
      ? { label: "Arbeidsmarkt", waarde: `${am.length} waarneming${am.length > 1 ? "en" : ""}`, gemeten: true }
      : { label: "Arbeidsmarkt", waarde: "geen waarneming", gemeten: false },
    g.reeks || g.cijfer
      ? { label: "Cijfer", waarde: "reeks gemeten", gemeten: true }
      : { label: "Cijfer", waarde: "geen reeks", gemeten: false },
    subs.length > 0
      ? { label: "Subsidie", waarde: String(subs[0]?.naam ?? "").slice(0, 28), gemeten: true }
      : { label: "Subsidie", waarde: "geen regeling", gemeten: false },
    vaardigheden.length > 0
      ? {
          label: "Vaardigheid",
          waarde: `${vaardigheden.length}${
            vaardigheden.some((v) => v.quick_win) ? ", quickwin" : ""
          }`,
          gemeten: true,
        }
      : { label: "Vaardigheid", waarde: "geen koppeling", gemeten: false },
  ];
}

export const lagenTelling = (kaart: EditieKaartData): number =>
  lagenVan(kaart).filter((l) => l.gemeten).length;

/* ── de bewijsregel ──────────────────────────────────────────────────────── */

export function bewijsRegel(kaart: EditieKaartData): { sterk: string; rest: string } {
  const g = grondVan(kaart);
  const bundels = bronbundels(kaart);
  const gezagen = bundels.map((b) => b.bron.gezag).filter((x): x is number => typeof x === "number");
  const gemiddeld = gezagen.length
    ? (gezagen.reduce((a, b) => a + b, 0) / gezagen.length).toFixed(1).replace(".", ",")
    : null;
  const jaren = bundels
    .map((b) => b.bron.datum?.slice(0, 4))
    .filter((x): x is string => Boolean(x))
    .sort();
  const eerste = jaren[0];
  const laatste = jaren[jaren.length - 1];
  const bereik = eerste && laatste && eerste !== laatste ? `${eerste}–${laatste}` : (eerste ?? null);

  const reeks = g.reeks as { punten?: unknown[]; regio?: string } | undefined;
  if (reeks?.punten) {
    return {
      sterk: `${reeks.punten.length} kwartalen`,
      rest: ["UWV", String(reeks.regio ?? "")].filter(Boolean).join(" · "),
    };
  }

  const trend = g.trend as Record<string, unknown> | undefined;
  const skill = g.skill as Record<string, unknown> | undefined;
  if (skill) {
    return {
      sterk: `${String(skill.trends ?? "?")} trends`,
      rest: `${String(skill.bronnen ?? "?")} bronnen`,
    };
  }

  if (bundels.length > 0) {
    const n = trend?.bronnen != null ? Number(trend.bronnen) : bundels.length;
    return {
      sterk: `${n} ${n === 1 ? "bron" : "bronnen"}`,
      // Methode v3: dragen de bronnen het wie-veld, dan zegt de regel WIE het
      // zegt ("✓ instituut ×2 · vakblad ×1") in plaats van het gezagsgemiddelde.
      rest: [
        bundels.some((b) => heeftBewijsvelden(b.bron))
          ? wieMaal(bundels.map((b) => b.bron))
          : gemiddeld
            ? `gezag ${gemiddeld}`
            : null,
        bereik,
      ]
        .filter(Boolean)
        .join(" · "),
    };
  }

  if (trend?.bronnen != null) return { sterk: `${String(trend.bronnen)} bronnen`, rest: "onder de trend" };
  // Geen verzonnen getallen: hier stond "1 trend · 0 vaardigheden" hard in de
  // code (gemeten in de poetsronde-audit 28-08) — precies het soort getal dat
  // dit platform belooft nooit te verzinnen. De vraag-kaart zégt nu wat waar
  // is: het antwoord moet van de lezer komen.
  if (kaart.vorm === "vraag_aan_jou") {
    return { sterk: "nog geen antwoord", rest: "jouw kennis is hier de ontbrekende laag" };
  }
  return { sterk: "geen documentbewijs", rest: "de maat staat in de open stand" };
}
